import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Route, Redirect } from 'react-router-dom';
import queryString from 'query-string';
import axios from 'axios';

import TagManager from 'react-gtm-module';
import * as amplitude from '@amplitude/analytics-browser';
import cookie from 'react-cookies';

// Components.
import AuthContainer from 'containers/AuthContainer';

// Actions.
import * as ProfileActions from 'actions/ProfileActions';
import * as ConfigActions from 'actions/ConfigActions';
import * as AnalyticsActions from 'actions/AnalyticsActions';
import * as ConsentsActions from 'actions/ConsentsActions';

import {
  getConsentsVersion,
  getPrivacyPolicyVersion,
  getTermsVersion,
  isAcceptAllCookies,
  getAcceptTypes,
  getConsentsAcceptAllCookiesText,
  getConsentsAcceptCookiesText,
  getConsentsRejectCookiesText,
  deleteAnalyticsCookies,
  deleteFunctionalCookies,
} from 'utils';

// Config.
import { serviceUserToken, facebookId, amplitudeApiKey } from 'config';

import {
  getAccessTokenFromBrowserCookie,
  hasWelltorySessionInBrowser,
} from 'utils/auth';

const EMPTY_FLAGS = {
  cookie_functionality: false,
  cookie_measurement: false,
  cookie_marketing: false,
};

function hasSessionOrJwtAuth() {
  return Boolean(
    hasWelltorySessionInBrowser() ||
    getAccessTokenFromBrowserCookie() ||
    cookie.load('csrftoken')
  );
}

@connect(
  state => ({
    auth: state.auth,
    profile: state.profile,
    consents: state.consents,
    config: state.config,
    i18n: state.i18n,
  }),
  dispatch => ({
    profileActions: bindActionCreators(ProfileActions, dispatch),
    configActions: bindActionCreators(ConfigActions, dispatch),
    consentsActions: bindActionCreators(ConsentsActions, dispatch),
  }),
)
export default class AuthRequireContainer extends React.Component {

  static defaultProps = {
    theme: 'light',
    isFree: false,
  };

  static contextTypes = {
    router: PropTypes.func.isRequired,
  };

  constructor(props, context) {
    super(props, context);

    this.state = {
      isLoadedIubendaScript: false,
    };
  }

  componentWillMount() {
    const { auth: { isAuthenticated } } = this.props;
    if (!isAuthenticated && !hasSessionOrJwtAuth()) {
      axios.defaults.headers.common['Authorization'] = `Token ${serviceUserToken}`;
    }
  }

  componentDidMount() {
    const { auth: { isAuthenticated }, profile, config, i18n } = this.props;
    // Fetch configs.
    if (isAuthenticated && !config.isFetched) {
      this.props.configActions.loadConfig();
    }
    if (isAuthenticated && !profile.isFetched) {
      this.props.profileActions.loadUserProfile();
    }

    if (this.props.consents.isFetched === false) {
      this.props.consentsActions.loadUserConsents();
    }

    window.addEventListener('iubenda:preference:expressed', this.onPreferenceExpressed);
    window.addEventListener('iubenda:consent:rejected', this.onIubendaConsentRejected);

    this.iubendaScript = document.createElement("script");
    this.iubendaScript.src =
      "https://embeds.iubenda.com/widgets/66e39e29-7226-4fd8-b12b-0d2cf96a0c67.js";
    this.iubendaScript.async = true;
    this.iubendaScript.onload = () => {
      this.onLoadedIubendaScript();
    };
    document.body.appendChild(this.iubendaScript);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { auth: { isAuthenticated } } = this.props;

    if (isAuthenticated !== nextProps.isAuthenticated && nextProps.isAuthenticated === true && nextProps.config.isFetched === false && nextProps.config.isFetching === false) {
      this.props.configActions.loadConfig();
    }

    if (isAuthenticated !== nextProps.isAuthenticated && nextProps.isAuthenticated === true && nextProps.profile.isFetched === false && nextProps.profile.isFetching === false) {
      this.props.profileActions.loadUserProfile();
    }

    let consentsTypes = EMPTY_FLAGS;
    if (nextProps.profile.isFetched === true && nextProps.consents.isFetched === true) {
      consentsTypes = nextProps.consents.consents.reduce((acc, consent) => {
        if (consent.consent_type.startsWith('cookie_')) {
          const key = consent.consent_type;
          acc[key] = consent.consent_status === 'granted';
        }
        return acc;
      }, { ...EMPTY_FLAGS })
    }
    else {
      let prefs = this.readIubendaPreferences();
      const consentsCookiesTypes = getAcceptTypes(prefs);
      consentsTypes = {
        ...EMPTY_FLAGS,
        ...consentsCookiesTypes,
      };
    }

    if (consentsTypes['cookie_measurement']) {
      this.initAnalytics();
    }

    if (isAuthenticated && nextProps.profile.profile.id !== undefined && consentsTypes['cookie_measurement']) {
      AnalyticsActions.identifyUser(nextProps.profile.profile);
      window.welltoryAnalitics = {
        profile: nextProps.profile.profile,
        config: nextProps.config.config,
      };
    }
    else if (isAuthenticated && nextProps.auth.isAuthenticated && nextProps.auth.isAuthenticated === false && consentsTypes['cookie_measurement']) {
      AnalyticsActions.resetUser();
      window.welltoryAnalitics = {};
    }
  }

  componentWillUnmount() {
    const { auth } = this.props;
    if (!auth.isAuthenticated) {
      delete axios.defaults.headers.common['Authorization'];
    }

    window.removeEventListener('iubenda:preference:expressed', this.onPreferenceExpressed);
    window.removeEventListener('iubenda:consent:rejected', this.onIubendaConsentRejected);

    if (this.iubendaScript?.parentNode) {
      this.iubendaScript.parentNode.removeChild(this.iubendaScript);
    }
  }

  updateConsents = (consents) => {
    this.props.consentsActions.updateUserConsents(consents);
  }

  readIubendaPreferences = () => {
    return window?._iub?.cs?.api?.getPreferences?.() ?? null;
  }

  onPreferenceExpressed = (event) => {
    const { router: { route } } = this.context;
    const pagePath = route.location.pathname;

    const prefs = this.readIubendaPreferences();
    const isAllAccepted = isAcceptAllCookies(prefs);
    const consents = getAcceptTypes(prefs);
    const consentRejectCookiesText = isAllAccepted ? getConsentsAcceptAllCookiesText() : getConsentsAcceptCookiesText();
    const consentsAccepted = Object.entries(consents).map(([consentKey, enabled]) => {
      return {
        consent_type: consentKey,
        consent_status: enabled === true ? 'granted': 'not_provided',
        consent_version: getConsentsVersion(),
        consent_method: 'button',
        consent_screen: pagePath,
        consent_text_shown: consentRejectCookiesText,
        privacy_policy_version: getPrivacyPolicyVersion(),
        terms_version: getTermsVersion(),
      };
    });

    this.updateConsents(consentsAccepted);
  }

  onIubendaConsentRejected = (event) => {
    const { router: { route } } = this.context;
    const pagePath = route.location.pathname;

    const prefs = this.readIubendaPreferences();
    const consents = getAcceptTypes(prefs);
    const consentRejectCookiesText = getConsentsRejectCookiesText();
    let isDeleteAnalyticsCookie = false;
    let isDeleteFunctionalCookie = false;
    const consentsAccepted = Object.entries(consents).map(([consentKey, enabled]) => {
      if (consentKey === 'cookie_measurement' && enabled === false) {
        isDeleteAnalyticsCookie = true;
      }
      if (consentKey === 'cookie_functionality' && enabled === false) {
        isDeleteFunctionalCookie = true;
      }
      return {
        consent_type: consentKey,
        consent_status: enabled === true ? 'granted': 'not_provided',
        consent_version: getConsentsVersion(),
        consent_method: 'button',
        consent_screen: pagePath,
        consent_text_shown: consentRejectCookiesText,
        privacy_policy_version: getPrivacyPolicyVersion(),
        terms_version: getTermsVersion(),
      };
    });

    if (isDeleteAnalyticsCookie) {
      deleteAnalyticsCookies();
    }

    if (isDeleteFunctionalCookie) {
      deleteFunctionalCookies();
    }

    this.updateConsents(consentsAccepted);
  }

  onLoadedIubendaScript = () => {
    this.setState({ isLoadedIubendaScript: true });
  }

  initAnalytics = () => {
    if (this.analyticsInitialized) return;
  
    TagManager.initialize({
      gtmId: 'GTM-WN829F3'
    });
  
    amplitude.init(amplitudeApiKey);

    (function(d, s, id){
      var js, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) {return;}
      js = d.createElement(s); js.id = id;
      js.src = "https://connect.facebook.net/en_US/sdk.js?xfbml=false";
      js.async = true;
      js.defer = true;
      fjs.parentNode.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'));

    const currentThis = this;
    window.fbAsyncInit = function() {
      window.FB.init({
        appId: facebookId,
        xfbml: false,
        version: 'v3.3',
      });
      currentThis.onInitFB();
    };
  
    this.analyticsInitialized = true;
  };

  onInitFB = () => {
    if (this.props.profile.profile.id !== undefined) {
      AnalyticsActions.identifyUserFB(this.props.profile.profile);
    }
  };

  render() {
    const {
      auth: { isAuthenticated },
      component: Component,
      theme,
      isFree,
      title,
      additionTitle,
      location,
      ...props
    } = this.props;
    return (
      <Route
        {...props}
        render={props =>
          (isAuthenticated || isFree)
            ? (
                <AuthContainer title={title} theme={theme} additionTitle={additionTitle} >
                  <Component />
                </AuthContainer>
              )
            : (
            <Redirect
              push
              to={{
                pathname: '/user/login',
                search: queryString.stringify({ ...queryString.parse(location.search), destination: location.pathname}),
                state: { from: location }
              }}
            />
          )
        }
      />
    )
  }

}
